import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SearchableDropdowns.css';

// Keep native selects as the source of truth so existing forms, validation,
// controlled values, option groups and change handlers retain their behavior.
export default function SearchableDropdowns() {
  const [select, setSelect] = useState(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [version, refresh] = useState(0);
  const [position, setPosition] = useState({});
  const panel = useRef(null);
  const input = useRef(null);
  const listId = useId();
  const close = (restore = false) => {
    if (restore && select?.isConnected) select.focus();
    setSelect(null);
  };

  useEffect(() => {
    const targetSelect = event => event.target?.closest?.('select:not([disabled])');
    const open = (element, text = '') => {
      setQuery(text); setActive(0); setSelect(element);
    };
    const pointer = event => {
      const element = targetSelect(event);
      if (!element || event.button > 0) return;
      event.preventDefault();
      if (event.type === 'pointerdown') open(element);
      // mousedown also cancels Chrome's native popup after pointerdown.
      if (event.type === 'mousedown' && !window.PointerEvent) open(element);
    };
    const click = event => {
      const element = targetSelect(event);
      if (!element) return;
      event.preventDefault();
      open(element);
    };
    const key = event => {
      const element = targetSelect(event);
      if (!element || event.ctrlKey || event.metaKey) return;
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key) || event.key.length === 1) {
        event.preventDefault(); open(element, event.key.length === 1 && event.key !== ' ' ? event.key : '');
      }
    };
    document.addEventListener('pointerdown', pointer, true);
    document.addEventListener('mousedown', pointer, true);
    document.addEventListener('click', click, true);
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('pointerdown', pointer, true);
      document.removeEventListener('mousedown', pointer, true);
      document.removeEventListener('click', click, true);
      document.removeEventListener('keydown', key, true);
    };
  }, []);

  useEffect(() => {
    if (!select) return;
    const originals = ['aria-expanded', 'aria-controls'].map(name => [name, select.getAttribute(name)]);
    select.setAttribute('aria-expanded', 'true'); select.setAttribute('aria-controls', listId);
    const reposition = () => {
      if (!select.isConnected || select.disabled || !select.getClientRects().length) { setSelect(null); return; }
      const rect = select.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 8;
      const above = rect.top - 8;
      const height = Math.min(340, Math.max(below, above));
      const width = Math.min(Math.max(rect.width, 240), window.innerWidth - 16);
      setPosition({ width, maxHeight: Math.max(100, height), left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        ...(below >= Math.min(340, above) ? { top: rect.bottom + 4 } : { bottom: window.innerHeight - rect.top + 4 }) });
    };
    reposition(); input.current?.focus();
    const outside = event => {
      if (!panel.current?.contains(event.target) && event.target !== select) setSelect(null);
    };
    const observer = new MutationObserver(() => { refresh(v => v + 1); reposition(); });
    observer.observe(select, { subtree: true, childList: true, attributes: true, characterData: true });
    const scroll = event => { if (!panel.current?.contains(event.target)) reposition(); };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', reposition);
    return () => {
      observer.disconnect();
      originals.forEach(([name, value]) => value === null ? select.removeAttribute(name) : select.setAttribute(name, value));
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', reposition);
    };
  }, [select, listId]);

  const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
  const options = select ? Array.from(select.options).map((option, index) => ({
    option, index, label: option.label, group: option.parentElement.tagName === 'OPTGROUP' ? option.parentElement.label : '',
    disabled: option.disabled || option.parentElement.disabled,
  })).filter(row => !row.option.hidden && normalize(`${row.label} ${row.group}`).includes(normalize(query.trim()))) : [];
  const enabled = options.filter(row => !row.disabled);
  const current = enabled[Math.min(active, Math.max(0, enabled.length - 1))];
  useEffect(() => {
    panel.current?.querySelector('[data-current="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [active, query, version]);
  const choose = row => {
    if (!row || row.disabled || select.disabled || !select.isConnected) return;
    if (select.multiple) row.option.selected = !row.option.selected;
    else select.selectedIndex = row.index;
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
    if (select.multiple) refresh(v => v + 1);
    else close(true);
  };
  if (!select) return null;
  const labelNode = select.labels?.[0]?.cloneNode(true);
  labelNode?.querySelectorAll('select').forEach(node => node.remove());
  const label = select.getAttribute('aria-label') || labelNode?.textContent?.trim() || select.getAttribute('name') || 'options';
  return createPortal(
    <div className={`searchable-dropdown${select.closest('.sidebar-wrapper') ? ' searchable-dropdown-dark' : ''}`} ref={panel} style={position} onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); }
      else if (event.key === 'Tab') { select.focus(); setSelect(null); }
      else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault(); setActive(n => Math.max(0, Math.min(enabled.length - 1, n + (event.key === 'ArrowDown' ? 1 : -1))));
      } else if (event.key === 'Enter') { event.preventDefault(); choose(current); }
    }}>
      <input ref={input} className="searchable-dropdown-search" type="search" role="combobox" aria-label={`Search ${label}`} aria-expanded="true" aria-controls={listId} aria-activedescendant={current ? `${listId}-${current.index}` : undefined}
        placeholder="Search…" value={query} onChange={event => { setQuery(event.target.value); setActive(0); }} autoComplete="off" />
      <div id={listId} role="listbox" aria-label={label} aria-multiselectable={select.multiple || undefined} className="searchable-dropdown-options">
        {options.map(row => <div key={row.index} id={`${listId}-${row.index}`} role="option" aria-selected={row.option.selected} aria-disabled={!!row.disabled}
          data-current={current?.index === row.index} className="searchable-dropdown-option" onMouseDown={event => event.preventDefault()} onClick={() => choose(row)}>
          <span>{row.label || '—'}{row.group && <small>{row.group}</small>}</span><span aria-hidden="true">{row.option.selected ? '✓' : ''}</span>
        </div>)}
        {!options.length && <div role="status" className="searchable-dropdown-empty">No matching options</div>}
      </div>
    </div>, document.body);
}
